import { INFINITE_SCROLL_PAGE_SIZE } from '../../../shared/config/constants'
import { t } from '../../../shared/config/translations'
import type {
	ClientCollection,
	ClientRelease,
	CollectionMasterYearUpdate,
} from '../../../shared/types/collection'
import type { ClientUser } from '../../../shared/types/user'
import { getDiscogsCollectionPage } from '../../api/collection'
import {
	getSettings,
	queueSettingsUpdate,
	updateSettings,
} from '../../api/settings'
import { getCurrentSettingsCache } from '../../cache/settings'
import {
	dismissCollectionImportNotice,
	setCollectionImportNoticeVisible,
} from '../../components/CollectionImportNotice'
import {
	type FiltersHandle,
	initFilters,
} from '../../components/filters/Filters'
import {
	type FiltersState,
	hasActiveFilters,
} from '../../components/filters/filterState'
import { renderDimReleasesFilter } from '../../components/filters/filterTemplates'
import { renderSpinner } from '../../components/Loader'
import { openOverlay } from '../../components/Overlay'
import { initReleaseList } from '../../components/ReleaseList'
import {
	initReleaseListItem,
	setReleaseListItemMasterYear,
	useLargeReleaseListCover,
} from '../../components/ReleaseListItem'
import { getReleaseUpdateDetail } from '../../components/release/releaseEvents'
import type { PageMountContext } from '../../router'
import {
	clearCollectionQueryCache,
	getCollectionStoreState,
	loadCollectionPageFromStore,
	subscribeCollectionMasterYearUpdates,
	updateCachedReleaseByInstanceId,
} from '../../stores/collectionStore'
import { updateCollectionFilterOptions } from './collectionFilterOptions'
import {
	applyCollectionFilterDefaultsToSettings,
	buildCollectionFilterResetState,
	buildUserDefaultCollectionFilters,
	type CollectionFiltersState,
	collectionFilters,
} from './collectionFilters'
import { getCollectionReleaseGroup } from './collectionGrouping'
import {
	nextCollectionLayout,
	readCollectionLayout,
	saveCollectionLayout,
} from './collectionLayout'
import { collectionLayoutButtonTemplate } from './collectionLayoutTemplates'
import { CollectionMasterEnrichment } from './collectionMasterEnrichment'
import {
	buildCollectionCacheKey,
	type CollectionPageContext,
} from './collectionPageData'
import {
	collectionErrorTemplate,
	collectionFiltersOverlayTemplate,
} from './collectionPageTemplates'
import { CollectionSideIndex } from './collectionSideIndex'
import { bindCollectionSyncControls } from './collectionSyncControls'
import { loadCollectionValue } from './collectionValue'

const COLLECTION_SEARCH_DEBOUNCE_MS = 300
const COLLECTION_OPTION_FILTER_KEYS = new Set<keyof FiltersState>([
	'favoritesOnly',
	'folderId',
	'media',
	'status',
	'genre',
	'style',
	'yearFrom',
	'yearTo',
])

export class CollectionPageController {
	private readonly elements: ReturnType<typeof getCollectionPageElements>
	private readonly sideIndex: CollectionSideIndex | null

	private defaultFilters: CollectionFiltersState
	private filterResetState: Partial<FiltersState>
	private readonly masterEnrichment: CollectionMasterEnrichment
	private readonly wasCollectionEmpty: boolean
	private currentSnapshotId: string | null = null
	private filterControls: FiltersHandle | null = null
	private overlayFilterControls: FiltersHandle | null = null
	private restoreFilterDefaultsButtonEl: HTMLButtonElement | null = null
	private setFilterDefaultsButtonEl: HTMLButtonElement | null = null
	private releaseList: ReturnType<typeof initReleaseList> | null = null
	private groupingDate = new Date()
	private isDestroyed = false
	readonly ready: Promise<void>
	private active = false
	private loadGeneration = 0
	private removeMasterYearUpdateListener: (() => void) | null = null
	private readonly pendingMasterYearUpdates = new Map<
		number,
		CollectionMasterYearUpdate
	>()
	private masterYearRenderFrame: number | null = null
	private searchDebounceTimer: number | null = null
	private searchQuery = ''
	private totalItems: number | null = null
	private layout = readCollectionLayout()
	private hasHiddenReleases = false
	private hiddenReleaseRequestGeneration = 0
	private readonly removeSyncControls: () => void

	constructor(
		private readonly rootEl: HTMLElement,
		private readonly user: ClientUser,
		private readonly context?: PageMountContext,
	) {
		const defaultFilters = buildUserDefaultCollectionFilters(user)
		collectionFilters.reset(defaultFilters)
		this.elements = getCollectionPageElements(rootEl)
		this.removeSyncControls = bindCollectionSyncControls(rootEl)
		this.sideIndex = this.elements.sideIndexEl
			? new CollectionSideIndex(
					this.elements.sideIndexEl,
					this.elements.collectionListEl,
				)
			: null

		if (this.elements.collectionListEl)
			this.elements.collectionListEl.dataset.layout = this.layout
		this.defaultFilters = defaultFilters
		this.filterResetState = buildCollectionFilterResetState(defaultFilters)
		this.wasCollectionEmpty = !!context?.collection?.wasEmpty
		this.currentSnapshotId = context?.collection?.snapshotId ?? null
		this.masterEnrichment = this.createMasterEnrichment()
		this.bindHandlers()
		this.ready = this.initPage()
	}

	private async initPage(): Promise<void> {
		if (this.wasCollectionEmpty) this.showInitialImportNotice()
		await this.initFilterControls()
		if (this.isDestroyed) return
		this.attachEventListeners()
		this.updateFilterButtonActiveState()
		await this.loadCollection()
	}

	private createMasterEnrichment(): CollectionMasterEnrichment {
		return new CollectionMasterEnrichment({
			isCurrentPageContext: (pageContext) =>
				this.isCurrentPageContext(pageContext),
			setMasterYearSortAvailability: (available) =>
				this.updateMasterYearSortAvailability(available),
		})
	}

	private bindHandlers(): void {
		this.handleImportNoticeClose = this.handleImportNoticeClose.bind(this)
		this.handleFilterButtonClick = this.handleFilterButtonClick.bind(this)
		this.handleDisplayControlsClick =
			this.handleDisplayControlsClick.bind(this)
		this.handleRestoreFilterDefaults =
			this.handleRestoreFilterDefaults.bind(this)
		this.handleSetFilterDefaults = this.handleSetFilterDefaults.bind(this)
		this.handleReleaseUpdated = this.handleReleaseUpdated.bind(this)
		this.handleSearchButtonClick = this.handleSearchButtonClick.bind(this)
		this.handleSearchInput = this.handleSearchInput.bind(this)
	}

	private async initFilterControls(): Promise<void> {
		if (this.elements.searchInputEl)
			this.elements.searchInputEl.value = this.searchQuery
		this.filterControls = await initFilters({
			signal: this.context?.signal,
			initialState: collectionFilters.get(),
			onChange: (state, changedKey) =>
				this.handleFilterChange(state, changedKey),
			options: {
				showFavoritesOnly: false,
				showFolder: false,
				showMedia: false,
				showStatus: false,
				showRating: false,
				showGenre: false,
				showStyle: false,
				showYearRange: false,
				dimReleases: false,
			},
			rootEl: this.rootEl,
		})
		this.renderDisplayControls()
	}

	private renderDisplayControls(): void {
		const controls = this.rootEl.querySelector(
			'#collection-display-controls',
		)
		if (controls) {
			const focusedId = controls.contains(document.activeElement)
				? document.activeElement?.id
				: undefined
			controls.innerHTML =
				(this.hasHiddenReleases
					? renderDimReleasesFilter(
							collectionFilters.get().dimReleases,
							'noFrame',
						)
					: '') + collectionLayoutButtonTemplate(this.layout)
			if (focusedId) {
				controls
					.querySelector<HTMLButtonElement>(`#${focusedId}`)
					?.focus()
			}
		}
	}

	private handleDisplayControlsClick(event: Event): void {
		if (!(event.target instanceof Element)) return
		if (event.target.closest('#col-layout-button')) {
			this.layout = nextCollectionLayout(this.layout)
			saveCollectionLayout(this.layout)
			if (this.elements.collectionListEl) {
				this.elements.collectionListEl.dataset.layout = this.layout
				if (this.layout === 'cover')
					for (const item of this.elements.collectionListEl.querySelectorAll<HTMLElement>(
						'li.release',
					))
						useLargeReleaseListCover(item)
			}
			this.renderDisplayControls()
			return
		}
		if (!event.target.closest('#col-dim-releases-filter')) return
		void this.handleFilterChange(
			{
				...collectionFilters.get(),
				dimReleases: !collectionFilters.get().dimReleases,
			},
			'dimReleases',
		)
	}

	private async handleFilterChange(
		state: FiltersState,
		changedKey?: keyof FiltersState,
	): Promise<void> {
		if (this.isDestroyed) return

		if (!changedKey) {
			collectionFilters.reset(state as CollectionFiltersState)
			this.renderDisplayControls()
			this.releaseList?.updateDimState(!!state.dimReleases)
			this.updateFilterButtonActiveState()
			this.updateDefaultFilterActionState()
			await Promise.all([
				this.loadCollection(),
				updateCollectionFilterOptions(collectionFilters.get()),
			])
			return
		}

		const changedState: Partial<CollectionFiltersState> =
			changedKey === 'status'
				? {
						status: state.status,
						favoritesOnly: state.favoritesOnly,
					}
				: { [changedKey]: state[changedKey] }
		collectionFilters.set(changedState)
		this.updateFilterButtonActiveState()
		this.updateDefaultFilterActionState()

		if (changedKey === 'dimReleases') {
			this.renderDisplayControls()
			this.releaseList?.updateDimState(!!state.dimReleases)
			const settings = getCurrentSettingsCache() ?? this.user.settings
			if (settings) {
				queueSettingsUpdate({
					...settings,
					dimReleaseIfHiddenOff: !state.dimReleases,
				})
			}
			this.defaultFilters = {
				...this.defaultFilters,
				dimReleases: !!state.dimReleases,
			}
			this.updateDefaultFilterActionState()
			return
		}

		const reload = this.loadCollection()
		if (COLLECTION_OPTION_FILTER_KEYS.has(changedKey)) {
			await Promise.all([
				reload,
				updateCollectionFilterOptions(collectionFilters.get()),
			])
			return
		}
		await reload
	}

	private handleFilterButtonClick(): void {
		openOverlay({
			html: collectionFiltersOverlayTemplate(),
			onMount: () => {
				this.updateTotalItems(this.totalItems)
				this.bindDefaultFilterActions()
				void this.initOverlayFilters()
			},
			options: { backLabel: t('nav.back'), link: false },
		})
	}

	private async initOverlayFilters(): Promise<void> {
		this.overlayFilterControls?.dispose()
		this.overlayFilterControls = await initFilters({
			signal: this.context?.signal,
			containerId: 'collection-overlay-filter-container',
			initialState: collectionFilters.get(),
			onChange: (state, changedKey) =>
				this.handleFilterChange(state, changedKey),
			options: {
				favoritesOnlyInStatus: true,
				showSort: false,
				showGenre: true,
				showStyle: true,
				showYearRange: true,
				dimReleases: false,
			},
		})
		await updateCollectionFilterOptions(collectionFilters.get())
		this.updateDefaultFilterActionState()
	}

	private bindDefaultFilterActions(): void {
		this.setFilterDefaultsButtonEl =
			document.querySelector<HTMLButtonElement>(
				'[data-set-filter-defaults]',
			)
		this.restoreFilterDefaultsButtonEl =
			document.querySelector<HTMLButtonElement>(
				'[data-restore-filter-defaults]',
			)
		this.setFilterDefaultsButtonEl?.addEventListener(
			'click',
			this.handleSetFilterDefaults,
		)
		this.restoreFilterDefaultsButtonEl?.addEventListener(
			'click',
			this.handleRestoreFilterDefaults,
		)
	}

	private async handleSetFilterDefaults(): Promise<void> {
		const settings = await getSettings()
		if (!settings) return

		this.setDefaultFilterActionsDisabled(true)
		const updatedSettings = applyCollectionFilterDefaultsToSettings(
			settings,
			collectionFilters.get(),
		)
		await updateSettings(updatedSettings)
		this.defaultFilters = buildUserDefaultCollectionFilters({
			...this.user,
			settings: updatedSettings,
		})
		this.filterResetState = buildCollectionFilterResetState(
			this.defaultFilters,
		)
		this.updateFilterButtonActiveState()
		this.updateDefaultFilterActionState()
	}

	private async handleRestoreFilterDefaults(): Promise<void> {
		this.setDefaultFilterActionsDisabled(true)
		await this.filterControls?.setState(this.defaultFilters, false)
		await this.overlayFilterControls?.setState(this.defaultFilters)
		this.updateDefaultFilterActionState()
	}

	private updateDefaultFilterActionState(): void {
		const matchesDefaults = !hasActiveFilters(
			collectionFilters.get(),
			this.defaultFilters,
		)
		this.setDefaultFilterActionsDisabled(matchesDefaults)
	}

	private setDefaultFilterActionsDisabled(disabled: boolean): void {
		if (this.setFilterDefaultsButtonEl) {
			this.setFilterDefaultsButtonEl.disabled = disabled
		}
		if (this.restoreFilterDefaultsButtonEl) {
			this.restoreFilterDefaultsButtonEl.disabled = disabled
		}
	}

	private updateFilterButtonActiveState(): void {
		if (!this.elements.filterButtonEl) return
		const filtersAreActive = hasActiveFilters(
			collectionFilters.get(),
			this.filterResetState,
		)
		this.elements.filterButtonEl.classList.toggle(
			'is-active',
			filtersAreActive,
		)
	}

	private attachEventListeners(): void {
		this.rootEl
			.querySelector('#collection-display-controls')
			?.addEventListener('click', this.handleDisplayControlsClick)
		this.removeMasterYearUpdateListener =
			subscribeCollectionMasterYearUpdates((updates) =>
				this.queueMasterYearUpdates(updates),
			)
		window.addEventListener(
			'collection:release-updated',
			this.handleReleaseUpdated,
		)
		this.elements.importNoticeCloseEl?.addEventListener(
			'click',
			this.handleImportNoticeClose,
		)
		this.elements.filterButtonEl?.addEventListener(
			'click',
			this.handleFilterButtonClick,
		)
		this.elements.searchButtonEl?.addEventListener(
			'click',
			this.handleSearchButtonClick,
		)
		this.elements.searchInputEl?.addEventListener(
			'input',
			this.handleSearchInput,
		)
	}

	private handleImportNoticeClose(): void {
		dismissCollectionImportNotice(this.elements.importNoticeEl)
	}

	private handleReleaseUpdated(event: Event): void {
		if (this.isDestroyed) return
		const { release, reason, confirmed } = getReleaseUpdateDetail(event)
		const merged = updateCachedReleaseByInstanceId(release) ?? release
		this.updateCollectionListItem(merged)
		if (reason === 'history' && confirmed) {
			if (merged.history?.isHidden) {
				this.hasHiddenReleases = true
				this.renderDisplayControls()
			}
			void this.refreshDimControlVisibility()
		}
	}

	private async refreshDimControlVisibility(): Promise<void> {
		const generation = ++this.hiddenReleaseRequestGeneration
		try {
			const collection = await getDiscogsCollectionPage(0, 1)
			if (
				this.isDestroyed ||
				generation !== this.hiddenReleaseRequestGeneration
			)
				return
			this.hasHiddenReleases =
				collection.hasHiddenReleases ?? this.hasHiddenReleases
			this.renderDisplayControls()
		} catch {
			// Keep the last known visibility until a collection refresh succeeds.
		}
	}

	private queueMasterYearUpdates(
		updates: CollectionMasterYearUpdate[],
	): void {
		if (this.isDestroyed) return
		updates.forEach((update) => {
			this.pendingMasterYearUpdates.set(update.instanceId, update)
		})
		if (this.masterYearRenderFrame !== null) return

		this.masterYearRenderFrame = window.requestAnimationFrame(() => {
			this.masterYearRenderFrame = null
			this.flushMasterYearUpdates()
		})
	}

	private flushMasterYearUpdates(): void {
		if (this.isDestroyed) return
		this.pendingMasterYearUpdates.forEach((update) => {
			const item = this.rootEl.querySelector<HTMLLIElement>(
				`.release-list li[data-release-id="${update.instanceId}"]`,
			)
			if (item) setReleaseListItemMasterYear(item, update)
		})
		this.pendingMasterYearUpdates.clear()
	}

	private handleSearchInput(): void {
		this.clearSearchDebounceTimer()
		this.searchDebounceTimer = window.setTimeout(() => {
			this.searchDebounceTimer = null
			this.applySearchInput()
		}, COLLECTION_SEARCH_DEBOUNCE_MS)
	}

	private handleSearchButtonClick(): void {
		this.clearSearchDebounceTimer()
		this.applySearchInput()
	}

	private clearSearchDebounceTimer(): void {
		if (this.searchDebounceTimer === null) return
		window.clearTimeout(this.searchDebounceTimer)
		this.searchDebounceTimer = null
	}

	private applySearchInput(): void {
		if (this.isDestroyed) return
		const nextSearchQuery = this.elements.searchInputEl?.value.trim() ?? ''
		if (nextSearchQuery === this.searchQuery) return
		this.searchQuery = nextSearchQuery
		void this.loadCollection()
	}

	show(): void {
		this.active = true
		void this.updateCollectionValue()
		this.releaseList?.resume()
		this.masterEnrichment.resume()
	}

	hide(): void {
		this.sideIndex?.cancelDrag()
		this.active = false
		this.releaseList?.pause()
		this.masterEnrichment.pause()
	}

	async refresh(): Promise<void> {
		await this.ready
		if (this.isDestroyed) return

		clearCollectionQueryCache()
		this.currentSnapshotId = getCollectionStoreState().readiness.snapshotId
		await this.loadCollection(true)
		await this.updateCollectionValue(true)
		await this.overlayFilterControls?.refreshFolders()
	}

	private async updateCollectionValue(refresh = false): Promise<void> {
		if (!this.elements.collectionValueEl || this.isDestroyed) return

		const settings = getCurrentSettingsCache() ?? this.user.settings
		const enabled = settings?.showCollectionValue !== false
		const shouldLoad =
			enabled && (refresh || this.elements.collectionValueEl.hidden)
		this.elements.collectionValueEl.hidden = !enabled
		if (shouldLoad)
			await loadCollectionValue(this.elements.collectionValueEl)
	}

	private async loadCollection(preserve = false): Promise<void> {
		if (!this.elements.collectionListEl || this.isDestroyed) return
		const generation = ++this.loadGeneration
		const count = preserve
			? Math.max(this.releaseList?.count ?? 0, INFINITE_SCROLL_PAGE_SIZE)
			: INFINITE_SCROLL_PAGE_SIZE
		this.releaseList?.pause()
		this.releaseList?.cancelPending()
		this.masterEnrichment.pause()
		if (!preserve) {
			this.releaseList?.destroy()
			this.releaseList = null
			this.elements.collectionListEl.replaceChildren(renderSpinner())
		}
		this.elements.collectionListEl.classList.add('is-loading')
		try {
			// Stage the full visible range so a failed refresh leaves the old list intact.
			const items: ClientRelease[] = []
			let firstPage: ClientCollection | null = null
			for (
				let offset = 0;
				offset < count;
				offset += INFINITE_SCROLL_PAGE_SIZE
			) {
				const page = await this.loadCollectionPage(offset)
				if (this.isDestroyed || generation !== this.loadGeneration)
					return
				if (!page) throw new Error('Collection snapshot changed')
				firstPage ??= page
				items.push(...page.releases)
				if (page.releases.length < INFINITE_SCROLL_PAGE_SIZE) break
			}
			if (!firstPage) throw new Error('Collection unavailable')
			this.applyLoadedCollectionPage(firstPage)
			this.renderReleaseList(items)
			await this.releaseList?.ready
			if (this.isDestroyed || generation !== this.loadGeneration) return
			this.elements.errorEl?.setAttribute('hidden', '')
			void this.masterEnrichment.queueVisible(items, {
				cacheKey: this.getCollectionCacheKey(),
				offset: 0,
				limit: INFINITE_SCROLL_PAGE_SIZE,
			})
			if (this.active) {
				this.releaseList?.resume()
				this.masterEnrichment.resume()
			}
		} catch (error) {
			if (this.isDestroyed || generation !== this.loadGeneration) return
			if (!preserve) this.handleError(t('common.failed'))
			if (preserve) throw error
		} finally {
			if (!this.isDestroyed && generation === this.loadGeneration) {
				this.elements.collectionListEl.classList.remove('is-loading')
				if (this.active) this.releaseList?.resume()
			}
		}
	}

	private applyLoadedCollectionPage(collection: ClientCollection): void {
		this.hasHiddenReleases =
			collection.hasHiddenReleases ??
			collection.releases.some((release) => !!release.history?.isHidden)
		this.renderDisplayControls()
		this.currentSnapshotId = collection.snapshotId ?? this.currentSnapshotId
		this.updateMasterYearSortAvailability(
			collection.masterSortAvailable ?? false,
		)
		this.masterEnrichment.updateCoveragePolling(
			collection.masterSortAvailable ?? false,
			collection.masterCoverageValidUntil,
		)
		this.updateTotalItems(collection.totalItems)
		const count = this.rootEl.querySelector<HTMLElement>(
			'[data-collection-item-count]',
		)
		if (count) {
			count.textContent = String(collection.collectionTotalItems)
			count.setAttribute(
				'aria-label',
				`${collection.collectionTotalItems} ${t('common.items')}`,
			)
			count.hidden = false
		}
	}

	private renderReleaseList(releases: ClientRelease[]): void {
		this.groupingDate = new Date()
		this.elements.collectionListEl?.replaceChildren()
		const fetchFunction = this.makeFetch()

		if (!this.releaseList) {
			if (!this.elements.collectionListEl) return
			this.releaseList = initReleaseList({
				containerEl: this.elements.collectionListEl,
				initialItems: releases,
				hasMore: releases.length < (this.totalItems ?? releases.length),
				fetchFunction,
				onFetchingChange: (isFetching, offset) => {
					this.toggleNextPageLoader(isFetching && offset > 0)
				},
				limit: INFINITE_SCROLL_PAGE_SIZE,
				dimReleases: collectionFilters.get().dimReleases,
				groupBy: (release) =>
					getCollectionReleaseGroup(
						release,
						collectionFilters.get().sort,
						this.groupingDate,
					),
				groupIdPrefix: 'collection-group',
				onGroupsChange: (groups) => this.sideIndex?.render(groups),
				largeCovers: () => this.layout === 'cover',
			})
			return
		}

		this.releaseList.updateDimState(collectionFilters.get().dimReleases)
		this.releaseList.reload(
			releases,
			fetchFunction,
			releases.length < (this.totalItems ?? releases.length),
		)
	}

	private toggleNextPageLoader(isVisible: boolean): void {
		this.elements.nextPageLoaderEl?.classList.toggle(
			'is-visible',
			isVisible,
		)
	}

	private showInitialImportNotice(): void {
		setCollectionImportNoticeVisible(
			this.elements.importNoticeEl,
			true,
			true,
		)
	}

	private updateMasterYearSortAvailability(available: boolean): void {
		const sortSelect = this.rootEl.querySelector<HTMLSelectElement>(
			'#col-sort-by-filter',
		)
		if (!sortSelect) return

		for (const option of Array.from(sortSelect.options)) {
			const isMasterYearSort = option.value.startsWith('masterYear')
			if (!isMasterYearSort) continue
			option.disabled = !available
		}
	}

	private isCurrentPageContext(context: CollectionPageContext): boolean {
		return (
			context.cacheKey ===
			this.getCollectionCacheKey(context.offset, context.limit)
		)
	}

	private updateCollectionListItem(release: ClientRelease): void {
		const li = this.rootEl.querySelector<HTMLElement>(
			`.release-list li[data-release-id="${release.id}"]`,
		)
		if (!li) return

		const updated = initReleaseListItem(
			release,
			Number(li.dataset.index || 0),
			collectionFilters.get().dimReleases,
		)
		if (this.layout === 'cover') useLargeReleaseListCover(updated)
		if (li.outerHTML === updated.outerHTML) return
		li.replaceWith(updated)
	}

	private updateTotalItems(total: number | null): void {
		if (total !== null) this.totalItems = total
		if (this.totalItems === null) return
		const label = `${this.totalItems} ${t('common.items')}`
		document
			.querySelectorAll<HTMLElement>('[data-collection-total-items]')
			.forEach((element) => {
				element.textContent = label
			})
	}

	private handleError(message: string): void {
		if (!this.elements.errorEl) return

		this.elements.errorEl.innerHTML = collectionErrorTemplate(message)
		this.elements.errorEl.removeAttribute('hidden')
		this.elements.errorEl
			.querySelector('#collection-retry-btn')
			?.addEventListener('click', () => {
				void this.loadCollection()
			})
	}

	private async loadCollectionPage(
		offset = 0,
		limit = INFINITE_SCROLL_PAGE_SIZE,
	): Promise<ClientCollection | null> {
		return loadCollectionPageFromStore({
			cacheKey: this.getCollectionCacheKey(offset, limit),
			filters: collectionFilters.get(),
			limit,
			offset,
			searchQuery: this.searchQuery,
		})
	}

	private getCollectionCacheKey(
		offset = 0,
		limit = INFINITE_SCROLL_PAGE_SIZE,
	): string {
		return buildCollectionCacheKey({
			filters: collectionFilters.get(),
			limit,
			offset,
			searchQuery: this.searchQuery,
			snapshotId: this.currentSnapshotId,
		})
	}

	private makeFetch() {
		return async (offset: number, limit: number) => {
			if (this.isDestroyed) return null
			const generation = this.loadGeneration
			const response = await this.loadCollectionPage(offset, limit)
			if (generation !== this.loadGeneration) return null
			if (this.isDestroyed) return null
			if (response) {
				void this.masterEnrichment.queueVisible(response.releases, {
					cacheKey: this.getCollectionCacheKey(offset, limit),
					limit,
					offset,
				})
			}
			return response ? { items: response.releases } : null
		}
	}

	dispose(): void {
		this.rootEl
			.querySelector('#collection-display-controls')
			?.removeEventListener('click', this.handleDisplayControlsClick)
		if (this.isDestroyed) return
		this.isDestroyed = true
		this.loadGeneration++
		this.removeSyncControls()
		this.clearSearchDebounceTimer()
		this.masterEnrichment.destroy()
		this.removeMasterYearUpdateListener?.()
		this.removeMasterYearUpdateListener = null
		if (this.masterYearRenderFrame !== null) {
			window.cancelAnimationFrame(this.masterYearRenderFrame)
			this.masterYearRenderFrame = null
		}
		this.pendingMasterYearUpdates.clear()
		this.releaseList?.destroy()
		this.releaseList = null
		this.filterControls?.dispose()
		this.overlayFilterControls?.dispose()
		this.filterControls = null
		this.overlayFilterControls = null

		window.removeEventListener(
			'collection:release-updated',
			this.handleReleaseUpdated,
		)

		this.elements.importNoticeCloseEl?.removeEventListener(
			'click',
			this.handleImportNoticeClose,
		)
		this.elements.filterButtonEl?.removeEventListener(
			'click',
			this.handleFilterButtonClick,
		)
		this.elements.searchButtonEl?.removeEventListener(
			'click',
			this.handleSearchButtonClick,
		)
		this.elements.searchInputEl?.removeEventListener(
			'input',
			this.handleSearchInput,
		)
		this.sideIndex?.dispose()
		this.setFilterDefaultsButtonEl?.removeEventListener(
			'click',
			this.handleSetFilterDefaults,
		)
		this.restoreFilterDefaultsButtonEl?.removeEventListener(
			'click',
			this.handleRestoreFilterDefaults,
		)
		this.setFilterDefaultsButtonEl = null
		this.restoreFilterDefaultsButtonEl = null
	}
}

function getCollectionPageElements(rootEl: HTMLElement) {
	return {
		collectionListEl: rootEl.querySelector<HTMLElement>('.release-list'),
		collectionValueEl:
			rootEl.querySelector<HTMLElement>('.collection-value'),
		errorEl: rootEl.querySelector<HTMLElement>('#collection-error'),
		filterButtonEl: rootEl.querySelector<HTMLButtonElement>(
			'#collection-filter-button',
		),
		importNoticeCloseEl: rootEl.querySelector<HTMLButtonElement>(
			'#collection-import-notice-close',
		),
		importNoticeEl: rootEl.querySelector<HTMLElement>(
			'#collection-import-notice',
		),
		nextPageLoaderEl: rootEl.querySelector<HTMLElement>(
			'.collection-next-page-loader',
		),
		searchButtonEl: rootEl.querySelector<HTMLButtonElement>(
			'#collection-search-button',
		),
		searchInputEl: rootEl.querySelector<HTMLInputElement>(
			'#collection-search-input',
		),
		sideIndexEl: rootEl.querySelector<HTMLElement>(
			'[data-collection-side-index]',
		),
	}
}
